/**
 * 语音识别纠错工具，用于修正 Azure STT 对专有名词的误识别
 */

/**
 * 日语专有名词纠错映射表
 */
const JAPANESE_CORRECTIONS: Record<string, string> = {
  野菜の溶性: 'やさいのようせい',
  野菜の養成: 'やさいのようせい',
  野菜の要請: 'やさいのようせい',
  野菜の容姓: 'やさいのようせい',
  野菜のようせい: 'やさいのようせい',
  '野菜 の溶性': 'やさいのようせい',
  '野菜 の養成': 'やさいのようせい',
  'やさい の溶性': 'やさいのようせい',
  'やさい の養成': 'やさいのようせい',
  野菜のヨウセイ: 'やさいのようせい',
  '野菜 の ようせい': 'やさいのようせい',
  '野菜のヨウ。生。': 'やさいのようせい',
  野菜のヨウ生: 'やさいのようせい',
  '野菜のヨウ。生': 'やさいのようせい',
  '野菜のヨウ、生': 'やさいのようせい',
  'やさいのヨウ。生。': 'やさいのようせい',
  やさいのヨウ生: 'やさいのようせい',
};

/**
 * 日语模糊匹配纠错规则（使用正则表达式）
 * 用于处理带有不确定空格和标点的变体
 */
const JAPANESE_FUZZY_RULES = [
  {
    pattern: /野菜\s*の\s*(溶性|養成|要請|容姓|ようせい|ヨウセイ)/g,
    replacement: 'やさいのようせい',
  },
  {
    pattern: /やさい\s*の\s*(溶性|養成|要請|容姓)/g,
    replacement: 'やさいのようせい',
  },
  {
    pattern: /野菜\s*の\s*ヨウ[\s。、．]*生[\s。、．]*/g,
    replacement: 'やさいのようせい',
  },
  {
    pattern: /やさい\s*の\s*ヨウ[\s。、．]*生[\s。、．]*/g,
    replacement: 'やさいのようせい',
  },
];

/**
 * 中文专有名词纠错映射表
 */
const CHINESE_CORRECTIONS: Record<string, string> = {};

const CHINESE_FUZZY_RULES: Array<{ pattern: RegExp; replacement: string }> = [];

/**
 * 英文专有名词纠错映射表
 */
const ENGLISH_CORRECTIONS: Record<string, string> = {};

const ENGLISH_FUZZY_RULES: Array<{ pattern: RegExp; replacement: string }> = [];

/**
 * 纠错配置接口
 */
interface CorrectionConfig {
  corrections: Record<string, string>;
  fuzzyRules: Array<{ pattern: RegExp; replacement: string }>;
}

/**
 * 各语言的纠错配置映射
 */
const LANGUAGE_CORRECTION_CONFIG: Record<string, CorrectionConfig> = {
  'ja-JP': {
    corrections: JAPANESE_CORRECTIONS,
    fuzzyRules: JAPANESE_FUZZY_RULES,
  },
  'zh-CN': {
    corrections: CHINESE_CORRECTIONS,
    fuzzyRules: CHINESE_FUZZY_RULES,
  },
  'en-US': {
    corrections: ENGLISH_CORRECTIONS,
    fuzzyRules: ENGLISH_FUZZY_RULES,
  },
};

/**
 * 应用纠错规则到文本
 */
const applyCorrectionRules = (text: string, config: CorrectionConfig): string => {
  let correctedText = text;

  // 优先应用精确匹配规则
  for (const [wrong, correct] of Object.entries(config.corrections)) {
    if (correctedText.includes(wrong)) {
      correctedText = correctedText.replace(new RegExp(wrong, 'g'), correct);
      console.log(`[Speech Correction] Exact: "${wrong}" -> "${correct}"`);
    }
  }

  // 再应用模糊匹配规则
  for (const rule of config.fuzzyRules) {
    if (rule.pattern.test(correctedText)) {
      const beforeCorrection = correctedText;
      correctedText = correctedText.replace(rule.pattern, rule.replacement);
      console.log(`[Speech Correction] Fuzzy: "${beforeCorrection}" -> "${correctedText}"`);
    }
  }

  return correctedText;
};

/**
 * 纠正语音识别文本中的专有名词错误
 * 根据语言代码自动应用对应的纠错规则
 * @param text 原始识别文本
 * @param lang 语言代码（如 'ja-JP', 'zh-CN', 'en-US'）
 * @returns 纠正后的文本
 */
export const correctSpeechText = (text: string, lang: string): string => {
  const config = LANGUAGE_CORRECTION_CONFIG[lang];

  if (!config || (Object.keys(config.corrections).length === 0 && config.fuzzyRules.length === 0)) {
    return text;
  }

  return applyCorrectionRules(text, config);
};
